import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  InputAdornment,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Email,
  CheckCircle,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const Login = () => {
  const { login, loading, error, clearError, isRateLimited, canRequestUnlock, requestUnlock } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [unlockRequested, setUnlockRequested] = useState(false);
  const [unlockMessage, setUnlockMessage] = useState('');
  const [showUnlockedSnackbar, setShowUnlockedSnackbar] = useState(false);

  // Check if user was redirected after successful unlock
  useEffect(() => {
    if (searchParams.get('unlocked') === 'true') {
      setShowUnlockedSnackbar(true);
      // Clean up URL
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
    
    // Clear global error
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const result = await login(formData);
    
    if (result.success) {
      navigate('/dashboard');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleRequestUnlock = async () => {
    if (!formData.username.trim()) {
      setFormErrors({ username: 'Please enter your username or email first' });
      return;
    }

    const result = await requestUnlock(formData.username);

    if (result.success) {
      setUnlockRequested(true);
      setUnlockMessage(result.message || 'If an account exists with this username/email, an unlock link will be sent.');
    } else {
      setUnlockMessage(result.error || 'Failed to request unlock. Please try again.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <LoadingSpinner message="Signing in..." />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography component="h1" variant="h4" gutterBottom>
                Novo Solution
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Business Intelligence Portal
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Super Admin Access Only
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
                {isRateLimited && canRequestUnlock && !unlockRequested && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      You can request an unlock email to regain access immediately.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<Email />}
                      onClick={handleRequestUnlock}
                      disabled={loading}
                    >
                      Send Unlock Email
                    </Button>
                  </Box>
                )}
              </Alert>
            )}

            {unlockRequested && (
              <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
                {unlockMessage}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Please check your email and click the unlock link to regain access.
                </Typography>
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username or Email"
                name="username"
                autoComplete="username"
                autoFocus
                value={formData.username}
                onChange={handleChange}
                error={!!formErrors.username}
                helperText={formErrors.username}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                This portal is restricted to authorized administrators only.
                <br />
                All access attempts are logged and monitored.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Snackbar for successful unlock */}
      <Snackbar
        open={showUnlockedSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowUnlockedSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowUnlockedSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
          icon={<CheckCircle />}
        >
          Account unlocked successfully! You can now log in.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Login;
